<script>
import i18n from '@/plugins/i18n';
import Properties from './properties.vue';
import Relations from './relations.vue';

export default {
    props: {
        Items: {
            type: Object,
            required: true,
        },

        CUT_NAME_THRESHOLD: {
            type: Number,
            default: 62,
        },

        CUT_FROM: {
            type: Number,
            default: 46,
        },
    },

    components: {
        Properties,
        Relations,
    },

    data() { 
        return {
            itemForProperties: null,
        }
    },

    computed: {
        HEADERS() {
            return [
                {
                    key: 'name',
                    flex: 6,
                },
                {
                    key: 'type',
                    flex: 1,
                },
                {
                    key: 'size',
                    flex: 1,
                },
                {
                    key: 'created_by_name',
                    flex: 2,
                },
                {
                    key: 'updated_on',
                    flex: 2,
                },
            ];
        },

        CONTEXT_MENU_ITEMS() {
            return [
                {
                    key: 'view',
                    text: i18n.t('documents.actions.view')
                },
                {
                    key: 'delete',
                    text: i18n.t('documents.actions.delete')
                },
                {
                    key: 'relations',
                    text: i18n.t('documents.actions.relations')
                },
                {
                    key: 'properties',
                    text: i18n.t('documents.actions.properties')
                },
            ];
        },

        DIRECTORY_CONTEXT_MENU_ITEMS() {
            return [
                {
                    key: 'view',
                    text: i18n.t('documents.actions.open')
                },
            ];
        },

        AVAILABLE_EVENTS() {
            return [
                'upload',
                'delete',
                'erase',
                'properties',
                'view',
                'relations',
            ];
        },
    },

    methods: {
        async HandleAction(key, data) {
            if (!this.AVAILABLE_EVENTS.includes(key)) {
                console.error(key + ' is not an available action!');
                return;
            }

            if (key === 'properties') {
                await this.SetItemForProperties(data);
                this.$refs.properties_popup.openPopup();
            } else if (key === 'relations') {
                await this.SetItemForProperties(data);
                this.$refs.relations_popup.openPopup();
            } else {
                this.$emit(key, data);
                this.$refs.properties_popup.startHideProcess();
                this.$refs.relations_popup.startHideProcess();
            }
        },

        async SetItemForProperties(data) {
            this.itemForProperties = data;;
        },
    },
}
</script>

<template>
    <div class="view-list">
        <div class="documents" v-if="Items.length > 0">
            <div class="document list-view-headers">
                <div v-for="header in HEADERS" :key="header.key" :style="{flex: header.flex}">
                    {{ $t(`documents.list_items.${header.key}`) }}
                </div>
            </div>
            <div class="document" v-for="(item, index) in Items" :key="item.id"
                :class="{ 'no-border': index === Items.length - 1 }" @dblclick="HandleAction('view', item)">
                <div v-for="header in HEADERS" :key="header.key" :style="{flex: header.flex}"
                    v-context-menu="{items: item.is_directory ? DIRECTORY_CONTEXT_MENU_ITEMS : CONTEXT_MENU_ITEMS, onItemClick: HandleAction, data: item}"
                >
                    <span v-if="header.key === 'updated_on'">
                        {{ item['updated_on'] || item['created_on'] | dateFormat }}
                    </span>
                    <span v-else-if="header.key === 'size'">
                        {{ item[header.key] | bytesFormat }}
                    </span>
                    <span v-else-if="header.key === 'name'">
                        <span v-if="item.name.length > CUT_NAME_THRESHOLD" v-sust-tooltip="{text: item.name, bgColor: 'var(--carbon-alternative)'}">
                            {{ item.name.substring(0, CUT_FROM) + '... .' + item.type }}
                        </span>
                        <span v-else>
                            {{ item.name }}
                        </span>
                    </span>
                    <span v-else-if="header.key === 'type'">
                        {{ item.is_directory ? $t('documents.directory') : item.type }}
                    </span>
                    <span v-else-if="header.key === 'created_by_name' && item.is_directory">
                        Sustainfinity
                    </span>
                    <span v-else>
                        {{ item[header.key] }}
                    </span>
                </div>
            </div>
        </div>
    
        <div v-else class="no-documents-linked">
            <div>
                {{ $t('documents.no_documents_here') }}
            </div>
    
            <div>
                <button class="sust-btn sust-btn-green upload-a-document" @click="HandleAction('upload')">
                    {{ $t('documents.upload_document') }}
                </button>
                <div>
                    {{ $t('documents.or_link_existing_document') }}
                </div>
            </div>
        </div>

        <sust-popup ref="properties_popup" :title="$t('documents.properties.header') + ` - ${itemForProperties ? itemForProperties.name : '???'}`" :header="true" :footer="false" size="sm"
            bgColor="var(--sustainfinity-faded-blue)"
        >
            <Properties :Item="itemForProperties" @action="HandleAction($event, itemForProperties)"/>
        </sust-popup>

        <sust-popup ref="relations_popup" :title="$t('documents.relations.header') + ` - ${itemForProperties ? itemForProperties.name : '???'}`" :header="true" :footer="false" size="md"
            bgColor="var(--sustainfinity-faded-blue)"
        >
            <Relations v-if="itemForProperties" :Id="itemForProperties.id"/>
        </sust-popup>
    </div>
</template>

<style lang='scss' scoped>
.view-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    >div {
        width: 100% !important;
        text-align: left;
    }

    >.header {
        color: var(--carbon-alternative);
        font-size: 1.4vw;
        margin-bottom: .5em;
    }

    >.documents {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;

        >.document {
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: flex-start;

            color: var(--carbon);
            font-size: 1.1vw;
            padding: .5em 0;
            border-bottom: thin solid rgb(202, 202, 202);

            >div {
                flex: 1;
            }

            &:last-of-type {
                border-bottom: 0 !important;
            }

            transition: background-color 250ms;
            &:hover {
                background-color: rgba(0, 0, 0, .1);
            }
        }

        >.list-view-headers {
            border-bottom: thin solid black !important;
            pointer-events: none !important;
        }
    }
}

.no-documents-linked {
    color: var(--carbon-alternative);
    font-size: .85vw;

    >* {
        margin-bottom: .5em;
    }

    >div {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;

        >button {
            margin-right: .5em;
            background-color: var(--carbon-alternative);
            color: whitesmoke;

            &:hover {
                background-color: var(--sustainfinity-green);
            }
        }
    }
}

.no-border {
    border: 0 !important;
}

.icon-button {
    font-size: 1vw;
    transition: color 250ms !important;

    > .fa-square-plus {
        color: var(--sustainfinity-green) !important;
    }

    > .fa-square-minus {
        color: var(--invalid-input) !important;
    }

    &:hover {
        > .fa-square-plus {
            color: var(--sustainfinity-green--hover) !important;
        }

        > .fa-square-minus {
            color: var(--invalid-input--hover) !important;
        }
    }
}
</style>